import {
  BASE_PATH_2_QUERY,
  BASE_PATH_3_QUERY,
  NEXT_SECTIONS_QUERY,
  RESUME_ID_QUERY,
} from '@glass/common/modules/pages/queryParams';
import removeEmptyKeys from '@glass/common/modules/utils/removeEmptyKeys';
import { RESUME_V2_FIELD, SECTIONS_FIELD_V2 } from '@glass/web/modules/resumes/fieldKeys';
import generateSectionStepKey from '@glass/web/modules/resumes/router/generateSectionStepKey';
import { miscSections, DOWNLOAD_SECTION } from '@glass/web/modules/resumes/router/sections';
import stepData, {
  DELIMITER,
  isValidBuilderSection,
} from '@glass/web/modules/resumes/router/stepData';
import { ROOT_STEP, INTRO_STEP } from '@glass/web/modules/resumes/steps';

import getBuilderSectionStepParams from '@glass/shared/components/Link/ResumeBuilderLink/getBuilderSectionStepParams';
import consoleDev from '@glass/shared/modules/logs/consoleDev';
import isMiscSection from '@glass/shared/modules/resumes/isMiscSection';

const extractNextSectionAndStep = (
  currentSectionSteps,
  stepIndex,
  currentSection,
  nextOverride,
) => {
  let nextStep = currentSectionSteps[stepIndex + 1];
  let nextSection = currentSection;
  if (!nextStep) {
    nextSection =
      nextOverride && isValidBuilderSection(nextOverride)
        ? nextOverride
        : stepData[currentSection].nextSection;
    if (nextSection) {
      nextStep = stepData[nextSection].steps[0];
    }
  }
  nextStep = nextStep === ROOT_STEP ? undefined : nextStep;

  return { nextStep, nextSection };
};

const isStepAlreadyCompleted = (step, nextStepParams, completedSteps) => {
  return (
    nextStepParams?.[BASE_PATH_3_QUERY] === step &&
    completedSteps?.[generateSectionStepKey(nextStepParams)]
  );
};

const getNextMiscStepParams = (overrideNextSection, currentSection, resume, resumeId) => {
  if (
    !overrideNextSection &&
    isMiscSection(currentSection) &&
    resume?.[RESUME_V2_FIELD]?.[SECTIONS_FIELD_V2]
  ) {
    const { [RESUME_V2_FIELD]: { [SECTIONS_FIELD_V2]: sections = [] } = {} } = resume;

    const currentIndex = miscSections?.findIndex(({ type }) => type === currentSection);
    const { type: nextMiscSection } =
      miscSections.find(
        ({ type }, idx) =>
          idx > currentIndex &&
          sections.find(({ type: sectionType, hidden }) => sectionType === type && !hidden),
      ) || {};
    if (nextMiscSection) {
      return getBuilderSectionStepParams({
        resumeId,
        [BASE_PATH_2_QUERY]: nextMiscSection,
      });
    }
  }
  return false;
};

const getBuilderNextStepParams = (
  {
    [BASE_PATH_2_QUERY]: currentSection,
    [BASE_PATH_3_QUERY]: currentStep = ROOT_STEP,
    [RESUME_ID_QUERY]: resumeId,
    [NEXT_SECTIONS_QUERY]: overrideNextSection = '',
    ...otherParams
  } = {},
  completedSteps,
  resume,
) => {
  if (!currentSection) {
    // todo: return first step?
    consoleDev.warn(`section: ${currentSection} invalid`);
    return {};
  }
  const { steps: currentSectionSteps } = stepData[currentSection] || {};
  const stepIndex = currentSectionSteps?.indexOf(currentStep);
  const [nextOverride, ...overrideArray] = overrideNextSection.split(DELIMITER);

  if (stepIndex < 0) {
    consoleDev.warn(`step: ${currentStep} invalid`);
    return {};
  }

  const { nextStep, nextSection } = extractNextSectionAndStep(
    currentSectionSteps,
    stepIndex,
    currentSection,
    nextOverride,
  );

  let nextStepParams = {
    [BASE_PATH_2_QUERY]: nextSection,
    [BASE_PATH_3_QUERY]: nextStep,
    [RESUME_ID_QUERY]: resumeId,
    ...otherParams,
  };

  const nextQuery =
    nextSection === nextOverride ? overrideArray.join(DELIMITER) : overrideNextSection;

  if (nextQuery) {
    nextStepParams[NEXT_SECTIONS_QUERY] = nextQuery;
  }

  // skip download intro when next step override is present
  // temporary to prevent going to intro step as it's not being shown as already completed
  if (
    (nextSection === DOWNLOAD_SECTION && nextOverride === DOWNLOAD_SECTION) ||
    isStepAlreadyCompleted(INTRO_STEP, nextStepParams, completedSteps)
  ) {
    nextStepParams = getBuilderSectionStepParams({ ...nextStepParams, skip: [INTRO_STEP] });
  }

  const nextMiscStepParams = getNextMiscStepParams(
    overrideNextSection,
    currentSection,
    resume,
    resumeId,
  );

  if (nextMiscStepParams) {
    nextStepParams = nextMiscStepParams;
  }

  return removeEmptyKeys(nextStepParams);
};

export default getBuilderNextStepParams;
